import {createSelector} from 'reselect';
import {getTranslations, getContactOwnerForm, getTravelerFeatures} from './store';
import {
    getBrandName,
    getHumanReadableBrandName,
    getPrivacyPolicyUrl,
    getTermsAndConditionsUrl
} from './system';
import {isInquiryModalOpen} from './modalStates';
import {getHostType} from './listing';
import {getUserData} from './userData';
import {labelMaker} from '../../server/utils/labelMaker';

const getInquiryModalPlatformLabels = createSelector(
    [getTranslations, getHostType],
    (trans, hostType) => {
        if (hostType.owner) {
            return {
                question: trans.questionForOwner,
                message: trans.messageToOwner
            };
        }
        return {
            question: trans.questionForManager,
            message: trans.messageToManager
        };
    }
);

/* eslint-disable complexity */
const getContactOwnerFormLabels = createSelector(
    [
        getTranslations,
        getBrandName,
        getHumanReadableBrandName,
        getContactOwnerForm,
        getInquiryModalPlatformLabels,
        getTravelerFeatures,
        getPrivacyPolicyUrl,
        getTermsAndConditionsUrl
    ],
    (
        trans,
        brandName,
        humanReadableBrandName,
        contactOwnerForm,
        inquiryModalPlatformLabels,
        travelerFeatures,
        privacyPolicyUrl,
        termsAndConditionsUrl
    ) => {
        const {question, message} = inquiryModalPlatformLabels;
        const {invalid, isFormValid, touched} = contactOwnerForm;

        const showFirstNameErrorMsg =
            !isFormValid && touched.has('firstName') && invalid.has('firstName');
        const showLastNameErrorMsg =
            !isFormValid && touched.has('lastName') && invalid.has('lastName');
        const showEmailAddressErrorMsg =
            !isFormValid && touched.has('emailAddress') && invalid.has('emailAddress');
        const showMessageErrorMsg =
            !isFormValid && touched.has('message') && invalid.has('message');
        const showTermsAndConditionsErrorMsg =
            !isFormValid &&
            touched.has('termsAndConditionsAgreed') &&
            invalid.has('termsAndConditionsAgreed');

        return {
            firstName: trans.firstName,
            lastName: trans.lastName,
            emailAddress: trans.emailAddress,
            message,
            firstNameErrorMsg: showFirstNameErrorMsg ? trans.firstNameErrorMsg : '',
            lastNameErrorMsg: showLastNameErrorMsg ? trans.lastNameErrorMsg : '',
            emailAddressErrorMsg: showEmailAddressErrorMsg ? trans.emailAddressErrorMsg : '',
            messageErrorMsg: showMessageErrorMsg ? trans.messageErrorMsg : '',
            termsAndConditionsAgreedErrorMsg: showTermsAndConditionsErrorMsg
                ? trans.termsAndConditionsAgreedErrorMsg
                : '',
            phoneNumber: travelerFeatures.inquiryPhoneIsMobile
                ? trans.mobileNumber
                : trans.phoneNumber,
            question,
            countryCodes: trans.countryCodes,
            inquirySubscribeText: labelMaker(trans.inquirySubscribeText, {
                brand: humanReadableBrandName
            }),
            inquiryTermsAgreementText: labelMaker(trans.inquiryTermsAgreementText, {
                brand: brandName,
                privacyPolicyUrl,
                termsAndConditionsUrl
            })
        };
    }
);
/* eslint-enable complexity */

const getContactOwnerFormHasTripDetailsErrors = createSelector(
    [getTranslations, getContactOwnerForm, isInquiryModalOpen],
    (trans, contactOwnerForm, inquiryModalOpen) => {
        const hasInvalidGuests = !!contactOwnerForm.invalid.has('guests');

        return inquiryModalOpen ? hasInvalidGuests : null;
    }
);

const getContactOwnerFormDetails = createSelector(
    [getContactOwnerForm, getContactOwnerFormLabels, getTravelerFeatures, getUserData],
    (contactOwnerForm, contactOwnerFormLabels, travelerFeatures, userData) => {
        const {
            firstName,
            lastName,
            countryCode,
            countryName,
            phoneNumber,
            emailAddress,
            message,
            termsAndConditionsAgreed,
            shouldSubscribeToNews,
            isFormValid
        } = contactOwnerForm;
        const {termsAndConditionsRequired} = travelerFeatures;
        return {
            labels: contactOwnerFormLabels,
            firstName,
            lastName,
            countryCode,
            countryName,
            phoneNumber,
            emailAddress,
            message,
            termsAndConditionsAgreed,
            termsAndConditionsRequired,
            shouldSubscribeToNews,
            isFormValid,
            userData
        };
    }
);

export default getContactOwnerFormDetails;
export {
    getInquiryModalPlatformLabels,
    getContactOwnerFormLabels,
    getContactOwnerFormDetails,
    getContactOwnerFormHasTripDetailsErrors
};
